import React from 'react'

import LabelAndTextInput from '@components/inputs/LabelAndTextInput'
import SelectBoxAndLabel from '@components/select/SelectBoxAndLabel'
import Title from '@components/title/Title'
import Pictogram from '@components/pictogram/Pictogram'
import house from '@assets/form/house.svg'
import room4 from '@assets/form/number-4.svg'
import padlock from '@assets/form/padlock.svg'
import { defaultInputValidator, numberValidator, requiredValidator } from '@/utils/validators'

import './Section.css'

export interface Section1Values {
	accommodationType: string
	rooms: string
	residenceType: string
	managedResidenceType: string
	lotNumber: string
	area: string
	parking: string
	usage: string
	vat: string
	price: string
}

const VAT_OPTIONS: { [key: string]: string } = {
	'': 'Sélectionner...',
	'5,5%': '5,5%',
	'7%': '7%',
	'10%': '10%',
	'20%': '20%',
}

const USAGE_OPTIONS: { [key: string]: string } = {
	'': 'Sélectionner...',
	'Résidence principale': 'Résidence principale',
	'Résidence secondaire': 'Résidence secondaire',
	'Investissement locatif': 'Investissement locatif',
}

const PARKING_OPTIONS: { [key: string]: string } = {
	'': 'Sélectionner...',
	'sans parking': 'Sans parking',
	'parking extérieur (aérien)': 'Parking extérieur (aérien)',
	'parking couvert (rdc)': 'Parking couvert (rdc)',
	'parking sous-sol': 'Parking sous-sol',
	'parking boxé': 'Parking boxé',
}

const RESIDENCE_TYPE_OPTIONS: { [key: string]: string } = {
	'': 'Sélectionner...',
	'Ancien': 'Ancien',
	'Neuf (accession libre)': 'Neuf (accession libre)',
	'Neuf (tourisme)': 'Neuf (tourisme)',
	'Neuf (étudiant)': 'Neuf (étudiant)',
	'Neuf (seniors)': 'Neuf (seniors)',
}

function getCombinedResidenceType(residenceType: string, managedResidenceType: string): string {
	if (residenceType === 'Neuf (résidences gérées)' && managedResidenceType) {
		return `Neuf (${managedResidenceType})`
	}
	return residenceType
}

function parseCombinedResidenceType(value: string): { residenceType: string; managedResidenceType: string } {
	switch (value) {
		case 'Neuf (tourisme)': return { residenceType: 'Neuf (résidences gérées)', managedResidenceType: 'tourisme' }
		case 'Neuf (étudiant)': return { residenceType: 'Neuf (résidences gérées)', managedResidenceType: 'étudiant' }
		case 'Neuf (seniors)': return { residenceType: 'Neuf (résidences gérées)', managedResidenceType: 'seniors' }
		default: return { residenceType: value, managedResidenceType: '' }
	}
}

const ACCOMMODATION_OPTIONS: { [key: string]: string } = {
	'': 'Sélectionner...',
	'Maison': 'Maison',
	'Appartement': 'Appartement',
}

interface Section1Props {
	values: Section1Values
	readOnly?: boolean
	onChange?: (key: keyof Section1Values, value: string) => void
}

function Section1(props: Section1Props) {
	const { onChange } = props
	const readonly = props.readOnly === false ? {} : { readOnly: true }
	// Only the broker can edit the summary, so only then is it validated, with
	// the same rules as on the creation page: a buyer could not fix it anyway.
	const editable = props.readOnly === false
	const fieldValidator = editable ? defaultInputValidator : undefined
	const roomsValidator = editable
		? (value: string) => [requiredValidator(value), numberValidator(value)]
		: undefined

	return (
		<div className={`section1${props.readOnly !== false ? ' section1--readonly' : ''}`}>
			<div className='sectionHeader'>
				{props.readOnly !== false && <img id='lock' src={padlock} alt='padlock' />}
				<Title>Récapitulatif de votre bien</Title>
				{props.readOnly !== false && <h3>(partie pré-remplie par votre conseiller)</h3>}
			</div>
			{props.readOnly !== false && (
				<div className='pictograms'>
					<Pictogram className='picto0' src={house} id='house'>
						{props.values.accommodationType || 'Type de bien'}
					</Pictogram>
					<Pictogram className='picto0' src={room4} id='room4'>
						{props.values.rooms ? `${props.values.rooms} pièces` : 'Nombre de pièces'}
					</Pictogram>
				</div>
			)}
			{props.readOnly === false && (
				<div className='fieldLine'>
					<SelectBoxAndLabel
						id='accommodationType'
						validator={fieldValidator}
						text='Type de bien'
						options={ACCOMMODATION_OPTIONS}
						value={props.values.accommodationType}
						onChange={value => onChange?.('accommodationType', value)}
					/>
					<LabelAndTextInput
						id='rooms'
						validator={roomsValidator}
						label='Nombre de pièces'
						placeholder='Ex : 3'
						value={props.values.rooms}
						onChange={value => onChange?.('rooms', value)}
					/>
				</div>
			)}
			<div className='formContent'>
				<div className='fieldLine'>
					{props.readOnly === false ? (
						<SelectBoxAndLabel
							id='residenceType'
							validator={fieldValidator}
							text='Type de résidence'
							options={RESIDENCE_TYPE_OPTIONS}
							value={getCombinedResidenceType(props.values.residenceType, props.values.managedResidenceType)}
							onChange={value => {
								const parsed = parseCombinedResidenceType(value)
								onChange?.('residenceType', parsed.residenceType)
								onChange?.('managedResidenceType', parsed.managedResidenceType)
							}}
						/>
					) : (
						<LabelAndTextInput
							id='residenceType'
							label='Type de résidence'
							placeholder='Neuf accession libre'
							value={getCombinedResidenceType(props.values.residenceType, props.values.managedResidenceType)}
							readOnly={readonly}
							onChange={value => console.log('residenceType: ' + value)}
						/>
					)}
					<LabelAndTextInput
						id='id'
						validator={fieldValidator}
						label='Numéro de lot'
						placeholder='no 008'
						value={props.values.lotNumber}
						readOnly={readonly}
						onChange={value => onChange?.('lotNumber', value)}
					/>
					<LabelAndTextInput
						id='area'
						validator={fieldValidator}
						label='Superficie'
						placeholder='100 m²'
						value={props.values.area}
						readOnly={readonly}
						onChange={value => onChange?.('area', value)}
					/>
				</div>
				<div className='fieldLine'>
					{props.readOnly === false ? (
						<SelectBoxAndLabel
							id='parking'
							validator={fieldValidator}
							text='Parking'
							options={PARKING_OPTIONS}
							value={props.values.parking}
							onChange={value => onChange?.('parking', value)}
						/>
					) : (
						<LabelAndTextInput
							id='parking'
							label='Parking'
							placeholder='1 place extérieure'
							value={props.values.parking}
							readOnly={readonly}
							onChange={value => console.log('parking: ' + value)}
						/>
					)}
					{props.readOnly === false ? (
						<SelectBoxAndLabel
							id='usage'
							validator={fieldValidator}
							text='Usage du bien'
							options={USAGE_OPTIONS}
							value={props.values.usage}
							onChange={value => onChange?.('usage', value)}
						/>
					) : (
						<LabelAndTextInput
							id='usage'
							label='Usage du bien'
							placeholder='Résidence principale'
							value={props.values.usage}
							readOnly={readonly}
							onChange={value => console.log('usage: ' + value)}
						/>
					)}
				</div>
				<div className='fieldLine'>
					{props.readOnly === false ? (
						<SelectBoxAndLabel
							id='vat'
							validator={fieldValidator}
							text='Taux de TVA'
							options={VAT_OPTIONS}
							value={props.values.vat}
							onChange={value => onChange?.('vat', value)}
						/>
					) : (
						<LabelAndTextInput
							id='vat'
							label='Taux de TVA'
							placeholder='20 %'
							value={props.values.vat}
							readOnly={readonly}
							onChange={value => console.log('vat: ' + value)}
						/>
					)}
					<LabelAndTextInput
						id='price'
						validator={fieldValidator}
						label='Prix'
						placeholder='450 000 €'
						value={props.values.price}
						readOnly={readonly}
						onChange={value => onChange?.('price', value)}
					/>
				</div>
			</div>
		</div>
	)
}

export default Section1
